import * as React from "react";
import styles from "./ManageDepartmentServices.module.scss";
import { CacheContext } from "../../../../../contexts/CacheContext";
import { useToasts } from "react-toast-notifications";
import { DepartmentServices, TRequestedService } from "intake_shared/src/types";
import store from "../../../../../store";
import {
    addNewDepartment,
    addRequestedService,
    deleteService,
    updateAllDepartmentAndServicesCache,
    updateDepartmentCache,
    updateRequestedServicesCache,
} from "../../../../../store/cache/requested_service";
import { Button } from "../../../bits/Button/Button";
import { FormCheckbox } from "../../../bits/FormCheckbox/FormCheckbox";
import { cn } from "../../../../helpers";
import { toTitleCase } from "../../../../../utils/utils";
import DeleteIcon from "../../../icons/Delete";
import { AlertDialog } from "../../../bits/AlertDialog/AlertDialog";
import { Input } from "../../../bits/Input/Input";
import CheckIcon from "../../../icons/Check";
import SaveCheckIcon from "../../../icons/SaveCheck";
import TrashIcon from "../../../icons/Trash";

type Dialogs = "map-service" | "delete-service" | "delete-department-service";

export default function ManageDepartmentServices() {
    const olc = React.useContext(CacheContext);
    const { addToast } = useToasts();
    const allAvailableServices = olc?.requestedServices || {};
    const allAvailableDepartments = olc?.departments || ({} as DepartmentServices);
    const allDepartmentsAndServices =
        olc?.allDepartmentAndServices || ({} as DepartmentServices);
    const [showDialog, setShowDialog] = React.useState<Dialogs | null>(null);

    const [selectedDepts, setSelectedDepts] = React.useState<TRequestedService>({});
    const [selectedServices, setSelectedServices] =
        React.useState<TRequestedService>({});
    const [selectedDepartment, setSelectedDepartment] = React.useState<string>("");
    const [selectedService, setSelectedService] = React.useState<string>("");

    const [newServiceLabel, setNewServiceLabel] = React.useState<string>("");
    const [newDepartment, setNewDepartment] = React.useState<string>("");

    async function saveDepartmentMapping() {
        try {
            await store.mapServicesToADepartment(selectedDepts, selectedServices);
            addToast("Selection saved", {
                appearance: "success",
                autoDismiss: true,
            });
            resetSelections();
            const returnedDepartmentsAndServices =
                await updateAllDepartmentAndServicesCache();
            if (returnedDepartmentsAndServices) {
                olc?.updateAllDeptAndServices(returnedDepartmentsAndServices);
            }
        } catch (e) {
            addToast(`Error saving selection. ${e.message}`, {
                appearance: "error",
                autoDismiss: false,
            });
        }
    }

    // function to delete a service from a department
    async function handleDeleteServiceFromDepartment(
        department: string,
        service: string
    ) {
        try {
            await store.deleteServiceFromDepartment(department, service);
            addToast("Service deleted successfully", {
                appearance: "success",
                autoDismiss: true,
            });
            const returnedDepartmentsAndServices =
                await updateAllDepartmentAndServicesCache();
            if (returnedDepartmentsAndServices) {
                olc?.updateAllDeptAndServices(returnedDepartmentsAndServices);
            }
        } catch (e) {
            addToast(`Error deleting service. ${e.message}`, {
                appearance: "error",
                autoDismiss: false,
            });
        }
    }

    // function to delete services
    async function handleDeleteService(service: string) {
        try {
            await deleteService(service);
            addToast("Service deleted successfully", {
                appearance: "success",
                autoDismiss: true,
            });
            const returnedServices = await updateRequestedServicesCache();
            if (returnedServices) {
                olc?.updateRequestedServices(returnedServices);
            }
        } catch (e) {
            addToast(`Error deleting service. ${e.message}`, {
                appearance: "error",
                autoDismiss: false,
            });
        }
    }

    function resetSelections() {
        setSelectedDepts({});
        setSelectedServices({});
    }

    // function to determine when button should be disabled
    function isEmptyObject(obj) {
        for (let key in obj) {
            if (obj.hasOwnProperty(key)) {
                return false;
            }
        }
        return true;
    }
    return (
        <div>
            <div className={styles.content}>
                <AlertDialog
                    open={showDialog === "delete-department-service"}
                    onClose={() => setShowDialog(null)}
                    title="Delete Service?"
                    description="Are you sure you want to delete this service from this department?"
                    onSaveClick={() => {
                        handleDeleteServiceFromDepartment(
                            selectedDepartment,
                            selectedService
                        );
                        setShowDialog(null);
                    }}
                    onCancelClick={() => setShowDialog(null)}
                    saveText="Delete"
                    isDeleting
                />
                <AlertDialog
                    open={showDialog === "delete-service"}
                    onClose={() => setShowDialog(null)}
                    title="Delete Service?"
                    description="Are you sure you want to delete this service?"
                    onSaveClick={() => {
                        handleDeleteService(selectedService);
                        setShowDialog(null);
                    }}
                    onCancelClick={() => setShowDialog(null)}
                    saveText="Delete"
                    isDeleting
                />
                <AlertDialog
                    open={showDialog === "map-service"}
                    onClose={() => setShowDialog(null)}
                    title="Map service to department?"
                    description="Are you sure you want to map this service to the selected department(s)?"
                    onSaveClick={() => {
                        saveDepartmentMapping();
                        setShowDialog(null);
                    }}
                />

                <div className={styles.contentCard}>
                    <div className={cn(styles.contentHeader, styles.cardTitle)}>
                        Departments & Services
                    </div>
                    <div className={styles.contentBody}>
                        {Object.entries(allDepartmentsAndServices).map(
                            ([department, services]) => (
                                <div
                                    key={department}
                                    className={styles.clinicianListItem}
                                >
                                    <div className={styles.departmentLabel}>
                                        {services.label} ({services?.practice_id})
                                    </div>
                                    {(Array.isArray(services.services) &&
                                        services.services.length > 0 && (
                                            <div
                                                className={styles.departmentServices}
                                            >
                                                {services.services.map((service) => (
                                                    <div
                                                        key={service}
                                                        className={
                                                            styles.departmentService
                                                        }
                                                    >
                                                        {toTitleCase(
                                                            service
                                                        )?.replace(/_/g, " ")}
                                                        <Button
                                                            variant="normal"
                                                            className={
                                                                styles.deleteButton
                                                            }
                                                            onClick={() => {
                                                                setSelectedDepartment(
                                                                    department
                                                                );
                                                                setSelectedService(
                                                                    service
                                                                );
                                                                setShowDialog(
                                                                    "delete-department-service"
                                                                );
                                                            }}
                                                        >
                                                            <DeleteIcon stroke="#17687F" />
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                        )) || (
                                        <div className={styles.noServices}>
                                            No service(s) added
                                        </div>
                                    )}
                                </div>
                            )
                        )}
                    </div>
                </div>
                <div className={styles.contentCard}>
                    <div className={styles.contentHeader}>
                        <div className={styles.cardTitle}>
                            Departments
                            <span className={styles.contentCount}>
                                {Object.keys(selectedDepts).length}
                            </span>
                        </div>
                        <div className={styles.cardButtons}>
                            <Button
                                variant="normal"
                                onClick={() => {
                                    const newSelectedDepts = {
                                        ...selectedDepts,
                                    };
                                    Object.entries(allAvailableDepartments).forEach(
                                        ([departments, label]) => {
                                            newSelectedDepts[departments] = label;
                                        }
                                    );
                                    setSelectedDepts(newSelectedDepts);
                                }}
                                className={styles.cardButton}
                            >
                                Select all
                            </Button>
                            <Button
                                variant="normal"
                                onClick={() => {
                                    setSelectedDepts({});
                                }}
                                className={styles.cardButton}
                            >
                                Clear
                            </Button>
                        </div>
                    </div>
                    <div className={styles.contentBody}>
                        {Object.entries(allAvailableDepartments).map(
                            ([department, deptDetails], idx) => {
                                return (
                                    <div
                                        key={`${department}-${idx}`}
                                        className={styles.departmentListItem}
                                    >
                                        <FormCheckbox
                                            label={deptDetails.label}
                                            checked={department in selectedDepts}
                                            onChange={(e) => {
                                                const newSelectedDepts = {
                                                    ...selectedDepts,
                                                };
                                                if (e.target.checked) {
                                                    newSelectedDepts[department] =
                                                        deptDetails.label;
                                                } else {
                                                    delete newSelectedDepts[
                                                        department
                                                    ];
                                                }
                                                setSelectedDepts(newSelectedDepts);
                                            }}
                                            noBorder
                                            labelClass={styles.departmentLabel}
                                        />
                                    </div>
                                );
                            }
                        )}
                        <div className={styles.addNewDepartment}>
                            <Input
                                placeholder="Add new department"
                                value={newDepartment}
                                onChange={(e) => {
                                    setNewDepartment(e.target.value);
                                }}
                            />
                            <Button
                                variant="normal"
                                type="submit"
                                className={styles.addNewDepartmentButton}
                                onClick={async (e) => {
                                    e.preventDefault();
                                    if (newDepartment) {
                                        await addNewDepartment(newDepartment);
                                        setNewDepartment("");
                                        const returnedDepartments =
                                            await updateDepartmentCache();
                                        if (returnedDepartments) {
                                            olc?.updateDepartments(
                                                returnedDepartments
                                            );
                                        }
                                        const returnedDepartmentsAndServices =
                                            await updateAllDepartmentAndServicesCache();
                                        if (returnedDepartmentsAndServices) {
                                            olc?.updateAllDeptAndServices(
                                                returnedDepartmentsAndServices
                                            );
                                        }
                                    }
                                }}
                            >
                                <SaveCheckIcon stroke="#666B77" />
                            </Button>
                        </div>
                    </div>
                </div>
                <div className={styles.contentCard}>
                    <div className={styles.contentHeader}>
                        <div className={styles.cardTitle}>
                            Services
                            <span className={styles.contentCount}>
                                {Object.keys(selectedServices).length}
                            </span>
                        </div>
                        <div className={styles.cardButtons}>
                            <Button
                                variant="normal"
                                onClick={() => {
                                    const newSelectedServices = {
                                        ...selectedServices,
                                    };
                                    Object.entries(allAvailableServices).forEach(
                                        ([service, label]) => {
                                            newSelectedServices[service] = label;
                                        }
                                    );
                                    setSelectedServices(newSelectedServices);
                                }}
                                className={styles.cardButton}
                            >
                                Select all
                            </Button>
                            <Button
                                variant="normal"
                                onClick={() => {
                                    setSelectedServices({});
                                }}
                                className={styles.cardButton}
                            >
                                Clear
                            </Button>
                        </div>
                    </div>
                    <div className={styles.contentBody}>
                        {Object.entries(allAvailableServices).map(
                            ([service, serviceLabel], idx) => {
                                return (
                                    <div
                                        key={`${service}-${idx}`}
                                        className={styles.departmentListItem}
                                    >
                                        <FormCheckbox
                                            label={serviceLabel}
                                            checked={service in selectedServices}
                                            onChange={(e) => {
                                                const newSelectedServices = {
                                                    ...selectedServices,
                                                };
                                                if (e.target.checked) {
                                                    newSelectedServices[service] =
                                                        serviceLabel;
                                                } else {
                                                    delete newSelectedServices[
                                                        service
                                                    ];
                                                }
                                                setSelectedServices(
                                                    newSelectedServices
                                                );
                                            }}
                                            noBorder
                                            labelClass={styles.departmentLabel}
                                        />
                                        <Button
                                            variant="normal"
                                            className={styles.serviceDeleteButton}
                                            onClick={() => {
                                                setSelectedService(service);
                                                setShowDialog("delete-service");
                                            }}
                                        >
                                            <TrashIcon stroke="#E23241" />
                                        </Button>
                                    </div>
                                );
                            }
                        )}
                        <div className={styles.addNewDepartment}>
                            <Input
                                placeholder="Add new service"
                                value={newServiceLabel}
                                onChange={(e) => {
                                    setNewServiceLabel(e.target.value);
                                }}
                            />
                            <Button
                                variant="normal"
                                type="submit"
                                className={styles.addNewServiceButton}
                                onClick={async (e) => {
                                    e.preventDefault();
                                    if (newServiceLabel) {
                                        await addRequestedService(newServiceLabel);
                                        const returnedServices =
                                            await updateRequestedServicesCache();
                                        if (returnedServices) {
                                            olc?.updateRequestedServices(
                                                returnedServices
                                            );
                                        }
                                    }
                                }}
                            >
                                <CheckIcon />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.footer}>
                <div className={styles.footerButtons}>
                    <Button
                        variant="primary"
                        disabled={
                            isEmptyObject(selectedDepts) ||
                            isEmptyObject(selectedServices)
                        }
                        onClick={() => setShowDialog("map-service")}
                    >
                        Map service to department
                    </Button>
                    <Button variant="secondary" onClick={resetSelections}>
                        Reset
                    </Button>
                </div>
            </div>
        </div>
    );
}
